import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { addLandingPage } from '@/lib/apis';
import { usePostHog } from 'posthog-js/react';

const MAX_URLS_PER_REQUEST = 10;

const normalizeUrl = (urlString: string): string => {
  const trimmed = urlString.trim();
  if (!trimmed) return '';
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return `https://${trimmed}`;
  }
  return trimmed;
};

const isValidUrl = (urlString: string): boolean => {
  try {
    const url = new URL(normalizeUrl(urlString));
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    if (!url.hostname || !url.hostname.includes('.')) return false;
    if (url.hostname.startsWith('.') || url.hostname.endsWith('.')) return false;
    if (!/^[a-zA-Z0-9.-]+$/.test(url.hostname)) return false;
    return true;
  } catch {
    return false;
  }
};

const parseUrls = (text: string): string[] =>
  text
    .split(/[\n,]+/)
    .map((url) => normalizeUrl(url.trim()))
    .filter((url) => url.length > 0);

interface AddLandingPageModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: number;
  organizationId: number;
  existingUrls: string[];
}

export const AddLandingPageModal = ({
  isOpen,
  onClose,
  businessId,
  organizationId,
  existingUrls,
}: AddLandingPageModalProps) => {
  const posthog = usePostHog();
  const [urlsText, setUrlsText] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!urlsText.trim()) {
      setError('Please enter at least one URL');
      return;
    }

    const urls = parseUrls(urlsText);

    if (urls.length === 0) {
      setError('Please enter at least one URL');
      return;
    }

    if (urls.length > MAX_URLS_PER_REQUEST) {
      setError(`Maximum ${MAX_URLS_PER_REQUEST} URLs per submission. You entered ${urls.length}.`);
      return;
    }

    const invalidUrls = urls.filter((url) => !isValidUrl(url));
    if (invalidUrls.length > 0) {
      setError(`Invalid URL(s): ${invalidUrls.join(', ')}`);
      return;
    }

    setIsSubmitting(true);

    let added = 0;
    let skipped = 0;
    const failed: string[] = [];

    for (const url of urls) {
      if (existingUrls.includes(url)) {
        skipped++;
        continue;
      }
      try {
        await addLandingPage(businessId, url);
        added++;
      } catch {
        failed.push(url);
      }
    }

    if (added > 0) {
      posthog?.capture('landing_page_added');
      queryClient.invalidateQueries({ queryKey: ['organizations', organizationId, 'business'] });
      queryClient.invalidateQueries({ queryKey: ['landingPagesDetails', businessId] });
    }

    setIsSubmitting(false);

    if (failed.length > 0 && added === 0 && skipped === 0) {
      setError(`Failed to add: ${failed.join(', ')}`);
      return;
    }

    const parts: string[] = [];
    if (added > 0) parts.push(`${added} landing page${added > 1 ? 's' : ''} added`);
    if (skipped > 0) parts.push(`${skipped} already existed`);
    if (failed.length > 0) parts.push(`${failed.length} failed`);
    setSuccess(parts.join(', ') + '.');

    setTimeout(() => handleClose(), 1500);
  };

  const handleClose = () => {
    setUrlsText('');
    setError('');
    setSuccess('');
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="text-lg font-semibold text-gray-900">Add Landing Pages</DialogTitle>
          <DialogDescription className="text-sm text-gray-700">
            Enter landing page URLs (one per line) to add them to your business. Maximum {MAX_URLS_PER_REQUEST} URLs per submission.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 pb-6">
          <Textarea
            value={urlsText}
            onChange={(e) => setUrlsText(e.target.value)}
            placeholder="E.g. https://example.com/products"
            className="min-h-[200px] text-gray-900"
            disabled={isSubmitting}
          />

          {success && (
            <div className="border-l-4 border-l-green-400 border-t border-r border-b border-green-400 rounded-lg bg-green-50 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-900">{success}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="border-l-4 border-l-red-400 border-t border-r border-b border-red-400 rounded-lg bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-900">{error}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white flex items-center space-x-2"
            >
              <span>{isSubmitting ? 'Adding...' : 'Add Landing Pages'}</span>
              {!isSubmitting && <ArrowRight className="h-4 w-4" />}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
